import type { EventCard as Card, EventsListResponse, LatLng, Locale } from '@spot/shared';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { popIn } from '../../components/ui/pressable';
import { Skeleton } from '../../components/ui/skeleton';
import { EventCard } from './event-card';

type ListQuery = UseInfiniteQueryResult<InfiniteData<EventsListResponse>>;

/** Loads the next page when the end of the list scrolls into view (button as a fallback). */
function useLoadMoreOnView(query: ListQuery) {
	const sentinel = useRef<HTMLDivElement>(null);
	const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
	useEffect(() => {
		const node = sentinel.current;
		if (node === null || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
					void fetchNextPage();
				}
			},
			{ rootMargin: '200px' },
		);
		observer.observe(node);
		return () => {
			observer.disconnect();
		};
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);
	return sentinel;
}

/** Paged event list with skeleton, error + retry and an empty state supplied by the caller. */
export function EventList({
	query,
	empty,
	timeZone,
	locale,
	viewer,
}: {
	query: ListQuery;
	empty: ReactNode;
	timeZone: string;
	locale: Locale;
	viewer: LatLng | null;
}) {
	const { t } = useTranslation();
	const sentinel = useLoadMoreOnView(query);
	const items = useMemo<Card[]>(
		() => query.data?.pages.flatMap((page) => page.items) ?? [],
		[query.data],
	);
	const now = new Date();

	if (query.isPending) return <ListSkeleton />;
	if (query.isError) {
		return (
			<div className="flex flex-col items-center gap-3 py-10 text-center">
				<p className="text-body text-text-muted">{t('errors.generic_body')}</p>
				<Button variant="secondary" onClick={() => void query.refetch()}>
					{t('errors.retry')}
				</Button>
			</div>
		);
	}
	if (items.length === 0) return <>{empty}</>;

	return (
		<ul className="flex flex-col gap-2">
			{items.map((event, index) => (
				<li key={event.id} {...popIn(index)}>
					<EventCard event={event} timeZone={timeZone} locale={locale} viewer={viewer} now={now} />
				</li>
			))}
			{query.hasNextPage && (
				<li className="flex justify-center py-2">
					<div ref={sentinel} />
					<Button
						variant="ghost"
						disabled={query.isFetchingNextPage}
						onClick={() => void query.fetchNextPage()}
					>
						{query.isFetchingNextPage ? t('activities.loading') : t('activities.load_more')}
					</Button>
				</li>
			)}
		</ul>
	);
}

function ListSkeleton() {
	return (
		<ul aria-busy="true" className="flex flex-col gap-2">
			{[0, 1, 2, 3].map((n) => (
				<li key={n}>
					<Skeleton className="rounded-card h-[72px] w-full" />
				</li>
			))}
		</ul>
	);
}
